import React, {useMemo, useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {learningPaths} from '../trainingData';
import {resources as baseResources} from '../resourcesData';

const copy = {
  en: {
    title: 'Publish content',
    helper: 'Upload once and make trusted learning content easy to find.',
    video: 'Upload video',
    document: 'Upload document',
    dropVideo: 'Drop an MP4 or MOV here',
    dropDocument: 'Drop a PDF, PPTX, DOCX, or XLSX here',
    browse: 'Browse files',
    pasteLink: 'Or paste a video link',
    upload: 'Upload',
    cancel: 'Cancel upload',
    titleLabel: 'Title',
    description: 'Description',
    category: 'Category',
    language: 'Language',
    trainer: 'Trainer',
    path: 'Learning path',
    order: 'Order in path',
    thumbnail: 'Thumbnail',
    transcript: 'Transcript (optional)',
    actionItems: 'Action items',
    addAction: 'Add action item',
    publish: 'Publish now',
    draft: 'Save as draft',
    type: 'Type',
    changeNote: 'Change note',
    replace: 'Replace existing resource',
    pin: 'Pin to Important Links',
    select: 'Select an option',
    required: 'This field is required.',
    replaceRequired: 'Add a change note when replacing a resource.',
    fileRequired: 'Select a file or paste a valid video link.',
    successVideo: 'Video saved successfully.',
    successDocument: 'Document saved successfully.',
    viewTraining: 'View in training library',
    viewResources: 'View in resource hub',
    providerNote: 'Files are simulated locally in this demo.',
    draftStatus: 'Draft',
    publishedStatus: 'Published'
  },
  es: {
    title: 'Publicar contenido',
    helper: 'Sube una vez y facilita encontrar contenido de aprendizaje confiable.',
    video: 'Subir video',
    document: 'Subir documento',
    dropVideo: 'Suelta un MP4 o MOV aquí',
    dropDocument: 'Suelta un PDF, PPTX, DOCX o XLSX aquí',
    browse: 'Buscar archivos',
    pasteLink: 'O pega un enlace de video',
    upload: 'Subir',
    cancel: 'Cancelar carga',
    titleLabel: 'Título',
    description: 'Descripción',
    category: 'Categoría',
    language: 'Idioma',
    trainer: 'Capacitador',
    path: 'Ruta de aprendizaje',
    order: 'Orden en la ruta',
    thumbnail: 'Miniatura',
    transcript: 'Transcripción (opcional)',
    actionItems: 'Acciones pendientes',
    addAction: 'Agregar acción',
    publish: 'Publicar ahora',
    draft: 'Guardar como borrador',
    type: 'Tipo',
    changeNote: 'Nota de cambio',
    replace: 'Reemplazar recurso existente',
    pin: 'Fijar en enlaces importantes',
    select: 'Selecciona una opción',
    required: 'Este campo es obligatorio.',
    replaceRequired: 'Agrega una nota de cambio al reemplazar un recurso.',
    fileRequired: 'Selecciona un archivo o pega un enlace de video válido.',
    successVideo: 'El video se guardó correctamente.',
    successDocument: 'El documento se guardó correctamente.',
    viewTraining: 'Ver en la biblioteca de capacitación',
    viewResources: 'Ver en el centro de recursos',
    providerNote: 'Los archivos se simulan localmente en esta demostración.',
    draftStatus: 'Borrador',
    publishedStatus: 'Publicado'
  }
};

const thumbnails = [
  'https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=900&q=80'
];

function DropZone({type, file, setFile, link, setLink, progress, onCancel, text}) {
  const accept = type === 'video' ? '.mp4,.mov' : '.pdf,.pptx,.docx,.xlsx';
  const selectFile = (event) => setFile(event.target.files?.[0] || null);
  const dropFile = (event) => {
    event.preventDefault();
    setFile(event.dataTransfer.files?.[0] || null);
  };

  return (
    <section className="upload-drop-section">
      <div className="upload-dropzone" onDragOver={(event) => event.preventDefault()} onDrop={dropFile}>
        <SafeIcon icon={FiIcons.FiUploadCloud} />
        <strong>{type === 'video' ? text.dropVideo : text.dropDocument}</strong>
        <small>{text.providerNote}</small>
        <label><input type="file" accept={accept} onChange={selectFile} />{text.browse}</label>
      </div>
      {file && <div className="selected-file"><SafeIcon icon={FiIcons.FiFile} /><span><strong>{file.name}</strong><small>{Math.max(1, Math.round(file.size / 1024))} KB</small></span>{progress > 0 && progress < 100 ? <button type="button" onClick={onCancel}>{text.cancel}</button> : <SafeIcon icon={FiIcons.FiCheckCircle} />}</div>}
      {file && progress > 0 && <div className="upload-progress"><span style={{width: `${progress}%`}} /></div>}
      {type === 'video' && <label className="video-link-field">{text.pasteLink}<input value={link} onChange={(event) => setLink(event.target.value)} placeholder="https://youtube.com / vimeo.com / drive.google.com" /></label>}
    </section>
  );
}

function MetadataForm({type, form, setForm, text, user, errors, resourceItems}) {
  const set = (key, value) => setForm((current) => ({...current, [key]: value}));
  const actionItems = form.actionItems || [];
  const categories = type === 'video' ? ['Products', 'Sales Process', 'Licensing', 'Leadership', 'Systems'] : ['Products', 'Sales Process', 'Licensing', 'Market Updates'];

  return (
    <div className="upload-form">
      <label>{text.titleLabel}<input value={form.title} onChange={(event) => set('title', event.target.value)} />{errors.title && <em>{text.required}</em>}</label>
      <label>{text.description}<textarea rows="3" value={form.description} onChange={(event) => set('description', event.target.value)} />{errors.description && <em>{text.required}</em>}</label>
      <div className="form-two-columns">
        <label>{text.category}<select value={form.category} onChange={(event) => set('category', event.target.value)}><option value="">{text.select}</option>{categories.map((item) => <option key={item}>{item}</option>)}</select>{errors.category && <em>{text.required}</em>}</label>
        <label>{text.language}<select value={form.language} onChange={(event) => set('language', event.target.value)}><option value="">{text.select}</option><option>EN</option><option>ES</option><option>Both</option></select>{errors.language && <em>{text.required}</em>}</label>
      </div>
      {type === 'video' ? <><div className="form-two-columns"><label>{text.trainer}<input value={user.name} readOnly /></label><label>{text.path}<select value={form.pathId} onChange={(event) => set('pathId', event.target.value)}><option value="">{text.select}</option>{learningPaths.map((path) => <option value={path.id} key={path.id}>{path.title}</option>)}</select></label></div>
        <label>{text.order}<input type="number" min="1" value={form.orderInPath} onChange={(event) => set('orderInPath', event.target.value)} /></label>
        <fieldset className="thumbnail-picker"><legend>{text.thumbnail}</legend>{thumbnails.map((image) => <button type="button" className={form.thumbnailUrl === image ? 'selected' : ''} onClick={() => set('thumbnailUrl', image)} key={image}><img src={image} alt="" /></button>)}</fieldset>
        <label>{text.transcript}<textarea rows="3" value={form.transcript} onChange={(event) => set('transcript', event.target.value)} /></label>
        <fieldset className="action-items"><legend>{text.actionItems}</legend>{actionItems.map((item, index) => <div key={index}><input value={item} onChange={(event) => set('actionItems', actionItems.map((value, itemIndex) => itemIndex === index ? event.target.value : value))} /><button type="button" onClick={() => set('actionItems', actionItems.filter((_, itemIndex) => itemIndex !== index))}><SafeIcon icon={FiIcons.FiX} /></button></div>)}{actionItems.length < 5 && <button type="button" className="add-action" onClick={() => set('actionItems', [...actionItems, ''])}><SafeIcon icon={FiIcons.FiPlus} /> {text.addAction}</button>}</fieldset></> : <><div className="form-two-columns"><label>{text.type}<select value={form.type} onChange={(event) => set('type', event.target.value)}><option value="">{text.select}</option><option value="presentation">Presentation</option><option value="guide">Guide</option></select>{errors.type && <em>{text.required}</em>}</label><label>{text.replace}<select value={form.replaceId} onChange={(event) => set('replaceId', event.target.value)}><option value="">{text.select}</option>{resourceItems.map((item) => <option value={item.id} key={item.id}>{item.title} · {item.currentVersion}</option>)}</select></label></div>
        <label>{text.changeNote}<input value={form.changeNote} onChange={(event) => set('changeNote', event.target.value)} />{errors.changeNote && <em>{text.replaceRequired}</em>}</label>
        <label className="toggle-field"><input type="checkbox" checked={form.isPinned} onChange={(event) => set('isPinned', event.target.checked)} /><span>{text.pin}</span></label></>}
    </div>
  );
}

function UploadPage({user, language, resourceItems, onPublishVideo, onPublishResource, onViewLibrary}) {
  const [tab, setTab] = useState('video');
  const [file, setFile] = useState(null);
  const [link, setLink] = useState('');
  const [progress, setProgress] = useState(0);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});
  const text = copy[language];
  const initialForm = useMemo(() => ({title: '', description: '', category: '', language: '', pathId: '', orderInPath: '', thumbnailUrl: thumbnails[0], transcript: '', actionItems: [''], type: '', changeNote: '', replaceId: '', isPinned: false}), [tab]);
  const [form, setForm] = useState(initialForm);

  const changeTab = (next) => {
    setTab(next);
    setFile(null);
    setLink('');
    setProgress(0);
    setErrors({});
    setForm({...initialForm});
  };
  const simulateUpload = () => {
    if (!file || progress === 100) return;
    setProgress(15);
    let value = 15;
    const timer = window.setInterval(() => {
      value += 18;
      setProgress(Math.min(value, 100));
      if (value >= 100) window.clearInterval(timer);
    }, 180);
  };
  const submit = (status) => {
    const nextErrors = {};
    ['title', 'description', 'category', 'language'].forEach((key) => {if (!form[key]) nextErrors[key] = true;});
    if (tab === 'video' && !file && !link.trim()) nextErrors.file = true;
    if (tab === 'document') {
      if (!file) nextErrors.file = true;
      if (!form.type) nextErrors.type = true;
      if (form.replaceId && !form.changeNote.trim()) nextErrors.changeNote = true;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    if (tab === 'video') onPublishVideo({...form, status, sourceName: file?.name || link, uploadedBy: user.name});
    else onPublishResource({...form, status, sourceName: file.name, uploadedBy: user.name});
    setToast({message: tab === 'video' ? text.successVideo : text.successDocument, target: tab === 'video' ? 'training' : 'resources'});
    setFile(null);
    setLink('');
    setProgress(0);
    setForm({...initialForm});
  };

  return <div className="page-content upload-page">
    <div className="page-title"><span className="eyebrow">GFI INTERNATIONAL</span><h1>{text.title}</h1><p>{text.helper}</p></div>
    <div className="upload-tabs"><button type="button" className={tab === 'video' ? 'active' : ''} onClick={() => changeTab('video')}><SafeIcon icon={FiIcons.FiVideo} />{text.video}</button><button type="button" className={tab === 'document' ? 'active' : ''} onClick={() => changeTab('document')}><SafeIcon icon={FiIcons.FiFileText} />{text.document}</button></div>
    <div className="upload-layout"><main><DropZone type={tab} file={file} setFile={(value) => {setFile(value); setProgress(0); window.setTimeout(simulateUpload, 50);}} link={link} setLink={setLink} progress={progress} onCancel={() => {setFile(null); setProgress(0);}} text={text} />{errors.file && <p className="upload-error">{text.fileRequired}</p>}<MetadataForm type={tab} form={form} setForm={setForm} text={text} user={user} errors={errors} resourceItems={resourceItems} /></main>
      <aside className="publish-panel"><span>{statusLabel(form.status, text)}</span><h2>{tab === 'video' ? text.video : text.document}</h2><p>{text.providerNote}</p><button type="button" className="primary-button" onClick={() => submit('published')}><SafeIcon icon={FiIcons.FiUpload} />{text.publish}</button><button type="button" className="save-draft-button" onClick={() => submit('draft')}>{text.draft}</button></aside>
    </div>
    <div className="mobile-publish-bar"><button type="button" onClick={() => submit('draft')}>{text.draft}</button><button type="button" onClick={() => submit('published')}>{text.publish}</button></div>
    {toast && <div className="publish-toast" role="status"><SafeIcon icon={FiIcons.FiCheckCircle} /><span>{toast.message}<button type="button" onClick={() => onViewLibrary(toast.target)}>{toast.target === 'training' ? text.viewTraining : text.viewResources}</button></span><button type="button" aria-label="Close" onClick={() => setToast(null)}><SafeIcon icon={FiIcons.FiX} /></button></div>}
  </div>;
}

function statusLabel(status, text) {
  return status === 'draft' ? text.draftStatus : text.publishedStatus;
}

export default UploadPage;